/**
 * Fallback chain — tries AI providers in priority order.
 * Skips unavailable providers, applies per-provider timeout,
 * respects caller-supplied AbortSignal, and reports failures to registry.
 */

import type { AICompletionRequest, AICompletionResponse } from './providers/base-provider';
import { getProvider, isProviderAvailable, reportProviderFailure, isDemoMode } from './provider-registry';
import { eventBus } from '@/core/events';

const DEFAULT_ORDER = ['ollama', 'anthropic', 'openrouter', 'browser'];
const DEMO_ORDER = ['demo'];
const DEFAULT_TIMEOUT_MS = 30_000;

export interface FallbackOptions {
  /** Provider IDs to try, in order. Defaults to all. */
  providers?: string[];
  /** Per-provider timeout in ms. */
  timeout?: number;
  /** Max retries per provider (default 1 = no retry). */
  retries?: number;
  /** Caller-supplied AbortSignal — aborts the entire chain when signaled. */
  signal?: AbortSignal;
  /**
   * Whether the demo provider may serve as the ultimate fallback
   * (default true). Set false for consumers whose output is stored or
   * shown as real content (translation, pipeline text) — canned demo
   * text there is corruption, not graceful degradation.
   */
  allowDemoFallback?: boolean;
}

/**
 * Execute an AI completion request using the fallback chain.
 * Tries each available provider in order until one succeeds.
 */
export async function executeWithFallback(
  request: AICompletionRequest,
  options?: FallbackOptions,
): Promise<AICompletionResponse> {
  // In demo mode, use demo provider exclusively.
  // Otherwise, append 'demo' as ultimate fallback so investigations
  // still work when real providers fail at runtime — unless the caller
  // opted out (content-producing consumers like translation).
  const baseOrder = isDemoMode() ? DEMO_ORDER : (options?.providers ?? DEFAULT_ORDER);
  const order = isDemoMode()
    ? baseOrder
    : (options?.allowDemoFallback === false ? [...baseOrder] : [...baseOrder, 'demo']);
  const timeout = options?.timeout ?? DEFAULT_TIMEOUT_MS;
  const maxAttempts = options?.retries ?? 1;
  const callerSignal = options?.signal;
  const errors: string[] = [];

  // Bail immediately if already aborted
  if (callerSignal?.aborted) {
    throw new Error('AI request aborted');
  }

  for (const providerId of order) {
    // Check caller abort between providers
    if (callerSignal?.aborted) {
      throw new Error('AI request aborted');
    }

    const provider = getProvider(providerId);
    if (!provider) continue;

    // Skip providers whose complete() is not real text generation (the
    // browser ML provider answers with a classification string) — their
    // response would be silently accepted as generated content.
    if (!provider.supportsGeneration) {
      errors.push(`${providerId}: no text generation`);
      continue;
    }

    const available = await isProviderAvailable(providerId);
    if (!available) {
      errors.push(`${providerId}: unavailable`);
      continue;
    }

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeout);

        // If caller aborts, also abort the per-provider controller
        const onCallerAbort = () => controller.abort();
        callerSignal?.addEventListener('abort', onCallerAbort, { once: true });

        try {
          // Race the completion against a timeout
          const result = await Promise.race([
            provider.complete(request),
            new Promise<never>((_, reject) => {
              controller.signal.addEventListener('abort', () => {
                // Distinguish caller abort from timeout
                if (callerSignal?.aborted) {
                  reject(new Error('AI request aborted'));
                } else {
                  reject(new Error(`${providerId}: timeout after ${timeout}ms`));
                }
              });
            }),
          ]);

          clearTimeout(timer);
          callerSignal?.removeEventListener('abort', onCallerAbort);
          return result;
        } catch (err) {
          clearTimeout(timer);
          callerSignal?.removeEventListener('abort', onCallerAbort);
          throw err;
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        errors.push(`${providerId} (attempt ${attempt + 1}): ${msg}`);

        // If this was a caller abort, stop the entire chain
        if (callerSignal?.aborted) {
          throw new Error('AI request aborted');
        }

        // Report failure to registry so it tracks consecutive failures
        reportProviderFailure(providerId);
      }
    }
  }

  // All providers failed
  const errorSummary = errors.join('; ');
  eventBus.emit('pipeline:error', {
    stage: 'ai-fallback',
    error: `All AI providers failed: ${errorSummary}`,
  });

  throw new Error(`All AI providers failed: ${errorSummary}`);
}
